// Per-dispatch order visibility — THE canonical scoping rule.
//
// Multiple DISPATCH users work the same system but must not see each other's
// orders. An order belongs to a dispatch when they created it (`createdBy`)
// or claimed it by assigning a store/driver (`dispatchId`). Orders with
// NEITHER field — admin-entered orders (the email workflow), legacy docs,
// externally written docs — form a SHARED POOL visible to every dispatch
// until one of them claims it with a forward-moving action (store/driver
// assignment). Editing an order's details deliberately does NOT claim it.
//
// Admin / super-admin / driver / store sessions are never scoped here (their
// own role filters are orthogonal and live in their pages). Applied once,
// centrally, in AppContext's value assembly so every current and future
// consumer of `orders` is automatically correct; the unscoped array remains
// available as `allOrders` for admin tooling.
//
// NOTE: this is a workflow boundary, not a security boundary — dispatch
// clients still download all orders (firestore.rules read stays any-authed
// for non-manager roles).

import { Order, User, UserRole } from '../types';

export const isOrderVisibleToDispatch = (order: Order, dispatchUserId: string): boolean =>
    order.createdBy === dispatchUserId ||
    order.dispatchId === dispatchUserId ||
    (!order.createdBy && !order.dispatchId);

// Returns the SAME array reference for non-dispatch sessions — callers (and
// the selective-subscription engine) rely on reference stability to avoid
// re-render churn.
export const visibleOrdersFor = (
    orders: Order[],
    user: Pick<User, 'id' | 'role'> | null,
): Order[] => {
    if (!user || user.role !== UserRole.DISPATCH) return orders;
    return orders.filter(o => isOrderVisibleToDispatch(o, user.id));
};

/**
 * Would this user find the order on the surface a "go to order" link drops
 * them on? Used by the group-chat mention modal to decide whether to offer
 * Navigate at all — every order surface applies its OWN filter, so a link can
 * land on a page that simply does not list the order:
 *
 *   dispatch → /orders shows only orders they own or that are still in the
 *              SHARED POOL (unclaimed, i.e. visible to every dispatch)
 *   store    → /store_hub lists only orders routed to that store
 *   driver   → /driver_app lists only their own deliveries
 *   admin    → /orders is unfiltered
 *   others   → no order surface at all (manager / warehouse / worker / packager)
 *
 * A reader who fails this check still gets the mention's detail sheet — they
 * just don't get a button that would dead-end on an empty list.
 */
export const canNavigateToOrder = (
    order: Pick<Order, 'createdBy' | 'dispatchId' | 'storeId' | 'driverId'>,
    user: Pick<User, 'id' | 'role'> | null | undefined,
): boolean => {
    if (!user) return false;
    switch (user.role) {
        case UserRole.OWNER:
        case UserRole.SUPER_ADMIN:
        case UserRole.ADMIN:
            return true;
        case UserRole.DISPATCH:
            return isOrderVisibleToDispatch(order as Order, user.id);
        case UserRole.STORE:
            return order.storeId === user.id;
        case UserRole.DRIVER:
            return order.driverId === user.id;
        default:
            return false;
    }
};
